const BEFORE_PHOTO = "https://customer-assets-jt897jd0.emergentagent.net/job_why-recover-app/artifacts/upydm72w_7E38F12E-63BD-4B0F-B285-F1AA8E7E3499.png";
const AFTER_PHOTO = "https://customer-assets-jt897jd0.emergentagent.net/job_why-recover-app/artifacts/kg0i9a1e_D376197A-F37D-4F8B-8DF0-CE48B362B775.png";

const Story = () => (
  <div className="fade-in max-w-4xl mx-auto px-6 py-20">
    <div className="mb-16 max-w-3xl">
      <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-6">My Story</div>
      <h1 className="font-display text-6xl md:text-7xl leading-[1.02]">
        <span className="gradient-text">60 seconds.</span>
      </h1>
      <p className="mt-6 font-display text-2xl md:text-3xl text-neutral-100 leading-tight">
        That's how long my heart stopped.
      </p>
    </div>

    <div className="grid md:grid-cols-2 gap-6 mb-16">
      <figure className="card-surface overflow-hidden group">
        <div className="relative aspect-[4/5]">
          <img src={BEFORE_PHOTO} alt="Before" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          <figcaption className="absolute bottom-6 left-6 right-6">
            <div className="text-xs uppercase tracking-widest text-neutral-400 mb-1">Before</div>
            <div className="font-display text-3xl text-neutral-100">The night I didn't wake up</div>
          </figcaption>
        </div>
      </figure>
      <figure className="card-surface overflow-hidden group">
        <div className="relative aspect-[4/5]">
          <img src={AFTER_PHOTO} alt="After" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
          <figcaption className="absolute top-6 left-6 right-6">
            <div className="text-xs uppercase tracking-widest text-teal-300 mb-1">After</div>
            <div className="font-display text-3xl text-neutral-100">Seven years, and counting</div>
          </figcaption>
        </div>
      </figure>
    </div>

    {/* Morgan Harper Nichols quote */}
    <blockquote className="max-w-3xl mx-auto mb-20 border-l-4 border-teal-400/70 pl-6 py-2" data-testid="story-quote">
      <p className="font-display text-2xl md:text-3xl text-neutral-100 leading-snug italic">
        Tell the story of the mountain you climbed. Your words could become a page in someone else's
        survival guide.
      </p>
      <footer className="mt-4 text-sm tracking-widest uppercase text-teal-300/80">— Morgan Harper Nichols</footer>
    </blockquote>

    <div className="max-w-3xl space-y-6 text-neutral-200 text-lg leading-relaxed">
      <p>
        I want to tell you why this site exists — because it exists because of one night that
        changed everything.
      </p>

      <p>
        I'd been drinking heavily for over fifteen years. That night, I mixed alcohol with too much
        Xanax. Severe alcohol poisoning. I passed out — unresponsive — on my dad's front lawn.
      </p>

      <p>
        My 14-year-old brother heard my phone going off, over and over. He came outside to check.
        He carried me in. He called 911.
      </p>

      <p>
        By the time paramedics arrived I wasn't responding to anything. Then I stopped breathing
        altogether. That's when CPR started — and the new adhesive defibrillator pads restarted my
        heart.
      </p>

      <p className="font-display text-2xl text-teal-200/90 pl-4 border-l-2 border-teal-500/60 py-2">
        For over a minute, I was gone.
      </p>

      <p>
        In fifteen years of heavy drinking, nothing like that had ever happened. Not once. But I
        learned my lesson. It's just unfortunate I learned it the hard way.
      </p>

      <p className="text-teal-200 font-medium">I never touched another drink.</p>
    </div>

    <div className="max-w-3xl mt-20 mb-6">
      <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-4">The waiting room I had to build myself</div>
      <h2 className="font-display text-4xl md:text-5xl leading-tight mb-8">
        <span className="text-neutral-100">Six months of research </span>
        <span className="gradient-text">before I ever sat down.</span>
      </h2>
    </div>

    <div className="max-w-3xl space-y-6 text-neutral-200 text-lg leading-relaxed">
      <p>
        My parents told me I needed therapy. They were right — but I wanted to walk in prepared. I
        wanted to be the right candidate.
      </p>
      <p>
        So I did the work first. About three months of research. Printed worksheets. Filled out
        questionnaires meant for clinicians. And I journaled — for nearly six months, sometimes
        twice a day.
      </p>
      <p>
        I already knew I had a problem. What I didn't know yet was the shape of it. The story
        underneath it. The parts of me that were older than the drinking.
      </p>
      <p>
        I also knew if I was going to make real changes, I had to start somewhere new. So I moved
        out west to live with my mother while I found my footing — until I felt steady enough to
        live on my own.
      </p>
      <p>
        Within a couple months, I found her. My therapist. We clicked immediately. Seven years
        later, I'm still under her care. And I've been sober the whole time.
      </p>
    </div>

    <div className="max-w-3xl mt-20 mb-6">
      <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-4">Why this exists</div>
      <h2 className="font-display text-4xl md:text-5xl leading-tight mb-8">
        <span className="gradient-text">This is that waiting room.</span>
      </h2>
    </div>

    <div className="max-w-3xl space-y-6 text-neutral-200 text-lg leading-relaxed">
      <p>
        My own story brought me here — to this idea of giving you a choice. Work through the
        pre-program, and see where it takes you.
      </p>
      <p>
        It's okay to be scared. Angry. Sad. Even confused. That's exactly what you're here to
        figure out. Is it addiction? Is there something going on with your mental health that you
        need help understanding? Or did you go through something so traumatic, so heartbreaking,
        that you can't figure it out on your own?
      </p>
      <p>You don't have to know yet. You just have to start.</p>
      <p>
        I'm not a therapist. I won't diagnose you. What I am is someone who was gone for a minute
        and came back — and I built the tools I wish I'd had between the decision to change and the
        first appointment.
      </p>
      <p>We are here to guide you. And we are here to make sure you feel safe.</p>
      <p className="font-display text-2xl text-teal-200/90 mt-8">— Gabrielle</p>
    </div>
  </div>
);

export default Story;
